// Страница /review/: любой открывает свой 3MF/STL и смотрит его в браузере (габариты, линейка) — файл никуда не уходит.
// «Показать заказчику» загружает сжатую модель в аккаунт: проект со ссылкой и PIN, комментарии заказчика и ответы мастера.
// Разбор и сжатие — в воркере; наружу уходит только E3M1 и превью.
import { decode } from './e3m-decode';
import { day, esc, fill, T, type Lang, type Tx } from './i18n';
import { CSS, openPanel, toast, type Comment, type Data, type Version } from './panel';
import { createView, type View } from './view3d';

type Ctx = { lang: Lang };
type Term = { projects: number | null; days: number; noBrand: boolean };
type Row = { id: number; title: string; expires: string; versions: number; thumb: string | null; open: number; link: boolean; closed: boolean };
type Project = Data & {
  id: number; title: string; note: string; expires: string; term: Term; link: { url: string; pin: string; lang: Lang } | null; closed: boolean; versions: Version[];
  profile: { name: string; logo: string | null; phone: string; telegram: string };
};
type Pick = { name: string; copies: number; plate: number };
type Item = { name: string; size: number[]; copies: number };
/** открытый и сжатый файл: части E3M1 по выбранным объектам */
type Prep = { name: string; items: Item[]; colors: string[]; parts: Uint8Array[] };
type Stash = { at: number; title: string; body: Uint8Array<ArrayBuffer> };

const MAX_SRC = 200 * 1024 * 1024, MAX_UP = 8 * 1024 * 1024, MAX_ITEMS = 40;
const LANG_NAMES: Record<Lang, string> = { uk: 'Українська', ru: 'Русский', en: 'English' };

// ---------- тело загрузки на время входа ----------
// Вход через Google и по ссылке из письма уводит со страницы — готовое тело ждёт в IndexedDB не дольше суток.
const IDB = 'gen3d-review', STORE = 'stash', KEY = 'upload', STASH_MS = 864e5;
function idb() {
  return new Promise<IDBDatabase>((res, rej) => {
    const r = indexedDB.open(IDB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function stashTx<R>(fn: (s: IDBObjectStore, out: { v?: R }) => void): Promise<R | undefined> {
  const d = await idb();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, 'readwrite'), out: { v?: R } = {};
    fn(tx.objectStore(STORE), out);
    tx.oncomplete = () => { d.close(); res(out.v); };
    tx.onerror = () => { d.close(); rej(tx.error); };
  });
}
const stashPut = (v: Stash) => stashTx((s) => { s.put(v, KEY); });
const stashDel = () => stashTx((s) => { s.delete(KEY); }).catch(() => {});
// забрать и удалить в одной транзакции: если вход закончился в двух вкладках, загрузит только одна
const stashTake = () => stashTx<Stash | null>((s, out) => {
  const g = s.get(KEY);
  g.onsuccess = () => { const v = g.result as Stash | undefined; out.v = v && Date.now() - v.at < STASH_MS ? v : null; if (v) s.delete(KEY); };
}).then((v) => v ?? null, () => null);

const PAGE_CSS = `
.panel-tags [data-share],.panel-tags [data-reset]{display:none}
.rv-p{display:flex;flex-direction:column;gap:16px;padding:8px 18px 20px}
.rv-p [hidden]{display:none!important}
.rv-intro h2{margin:0 0 6px;font:700 18px/1.25 var(--font-head)}
.rv-intro p{margin:0 0 12px;color:var(--muted);line-height:1.5}
.rv-term{margin:0;font-size:13px;color:var(--muted);line-height:1.4}
.rv-tip{margin:0;padding:10px 12px;border-radius:12px;border-left:3px solid var(--accent);background:color-mix(in srgb,var(--accent) 8%,transparent);font-size:14px;line-height:1.4}
.rv-tip a{color:var(--accent-text);font-weight:600}
.rv-mine{display:flex;flex-direction:column;gap:10px}
.rv-mine>h3{margin:0;font:700 13px/1.2 var(--font);letter-spacing:.04em;text-transform:uppercase;color:var(--muted)}
.rv-mine .rv-msg:empty{display:none}
.rv-projs{display:flex;flex-direction:column;gap:8px;margin:0;padding:0;list-style:none}
.rv-proj{display:flex;align-items:center;gap:12px;width:100%;min-height:64px;padding:8px;border-radius:14px;border:1.5px solid var(--line);background:var(--surface);color:var(--text);text-align:left;font:inherit;cursor:pointer}
.rv-proj:hover{border-color:var(--accent)}
.rv-proj img,.rv-proj .ph{width:56px;height:56px;border-radius:10px;object-fit:cover;background:var(--surface-2);flex:none}
.rv-proj b{display:block;font-weight:700;overflow-wrap:anywhere}
.rv-proj small{display:block;color:var(--muted);font-size:13px;margin-top:2px}
.rv-proj .new{color:var(--accent-text);font-weight:600}
.rv-box{display:flex;flex-direction:column;gap:8px;padding:12px;border-radius:14px;border:1px solid var(--line);background:var(--surface)}
.rv-box h3{margin:0;font:700 13px/1.2 var(--font);letter-spacing:.04em;text-transform:uppercase;color:var(--muted)}
.rv-fname{font-weight:700;overflow-wrap:anywhere}
.rv-link{font:500 14px/1.4 var(--font-mono);overflow-wrap:anywhere;user-select:all}
.rv-link b{font-size:18px;letter-spacing:.12em}
.rv-drop{display:flex;flex-direction:column;align-items:center;gap:6px;padding:16px 12px;border:1.5px dashed var(--line);border-radius:12px;text-align:center}
.rv-drop.over{border-color:var(--accent);background:color-mix(in srgb,var(--accent) 7%,transparent)}
.rv-drop small{color:var(--muted);font-size:13px}
.rv-picks{display:flex;flex-direction:column;gap:2px;max-height:260px;overflow:auto}
.rv-picks label{display:flex;align-items:center;gap:10px;min-height:44px;font-size:14px;cursor:pointer}
.rv-picks input{width:20px;height:20px;accent-color:var(--accent);flex:none}
.rv-bar{height:6px;border-radius:3px;background:var(--surface-2);overflow:hidden}
.rv-bar i{display:block;height:100%;background:var(--accent);transition:width .2s}
.rv-p select{min-height:44px;padding:0 10px;border-radius:12px;border:1.5px solid var(--line);background:var(--surface);color:var(--text);font:15px var(--font)}
.rv-back{align-self:flex-start;min-height:44px;padding:0;border:0;background:none;color:var(--accent-text);font:600 15px var(--font);cursor:pointer}
.rv-cab{position:absolute;left:10px;right:66px;top:10px;z-index:2;display:flex;align-items:center;gap:8px;min-height:44px;padding:0 5px 0 12px;box-sizing:border-box;border-radius:999px;border:1px solid var(--line);background:color-mix(in srgb,var(--surface) 92%,transparent);font:600 14px/1.2 var(--font);color:var(--text)}
.rv-path{display:flex;align-items:center;gap:6px;flex:1;min-width:0}
.rv-path a,.rv-path button{display:flex;align-items:center;gap:6px;min-height:44px;padding:0;border:0;background:none;color:var(--accent-text);font:inherit;text-decoration:none;white-space:nowrap;cursor:pointer;flex:none}
.rv-path a svg{width:18px;height:18px;flex:none}
.rv-path .sep{color:var(--muted);flex:none}
.rv-path b{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.rv-st{flex:none;display:flex;align-items:center;gap:6px;min-height:34px;padding:0 10px;border-radius:999px;background:var(--surface-2);color:var(--muted);font-size:13px;white-space:nowrap}
.rv-st.on{background:var(--ok-bg,color-mix(in srgb,var(--ok) 14%,transparent));color:var(--ok)}
.rv-st svg{width:16px;height:16px;flex:none}
[data-stage]:has(>.rv-cab) .rv-hint{top:64px}
[data-stage]{isolation:isolate} /* CSS2D-подписи ставят себе z-index — без этого вылезают поверх панели */
@media (min-width:900px){.rv-tools{right:calc(var(--panel-w) + 32px);top:16px}.rv-cab{left:16px;top:16px;right:calc(var(--panel-w) + 88px)}}
@media (max-width:599px){.rv-cab .mid{display:none}.rv-st{padding:0;width:34px;justify-content:center}.rv-st .l{display:none}}
.rv-prof{display:flex;flex-direction:column;gap:8px;margin-top:4px;padding-top:12px;border-top:1px solid var(--line)}
.rv-prof>.btn{align-self:flex-start}
.rv-prof.warn{margin-top:8px;padding:12px;border:0;border-left:3px solid var(--beta);border-radius:12px;background:var(--beta-bg)}
.rv-pwarn{display:flex;gap:8px;margin:0;font-size:14px;line-height:1.45;color:var(--text)}
.rv-pwarn svg{width:18px;height:18px;flex:none;margin-top:1px;color:var(--beta)}
.rv-pfill{border-color:var(--accent)!important;color:var(--accent-text)!important;background:var(--surface)!important}
.rv-pcard{display:flex;align-items:center;gap:10px;min-height:44px}
.rv-pcard img{width:40px;height:40px;border-radius:10px;object-fit:contain;background:var(--surface-2);flex:none}
.rv-pcard b{flex:1;min-width:0;overflow-wrap:anywhere}
.rv-pcard i{display:grid;place-items:center;width:28px;height:28px;border-radius:50%;background:var(--surface-2);color:var(--muted);flex:none}
.rv-pcard i svg{width:15px;height:15px}
.rv-pcard a{display:flex;align-items:center;min-height:44px;padding:0 4px;color:var(--accent-text);font-weight:600;flex:none}
.rv-danger{color:var(--bad)!important}
.rv-idle{position:absolute;inset:0;display:grid;place-items:center;padding:16px;text-align:center;color:var(--muted);font:600 15px/1.4 var(--font);pointer-events:none}
.rv-idle.over{color:var(--accent-text);background:color-mix(in srgb,var(--accent) 7%,transparent)}
.rv-badge{position:absolute;left:10px;top:10px;z-index:2;max-width:calc(100% - 76px);padding:6px 10px;border-radius:999px;background:color-mix(in srgb,var(--surface) 90%,transparent);border:1px solid var(--line);font:600 12px/1.25 var(--font);color:var(--muted)}
.rv-dlg{border:0;border-radius:18px;padding:20px;width:min(440px,calc(100vw - 32px));box-sizing:border-box;background:var(--surface);color:var(--text)}
.rv-dlg::backdrop{background:rgba(0,0,0,.45)}
.rv-dlg h2{margin:0 0 8px;font:700 19px/1.25 var(--font-head)}
.rv-dlg p{margin:0 0 12px;line-height:1.5}
.rv-dlg .rv-msg{margin:8px 0 0}
.rv-dlg .rv-msg:empty{display:none}
`;

const svg = (d: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const ICON = {
  user: svg('<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>'),
  lock: svg('<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>'),
  link: svg('<path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>'),
  tg: svg('<path d="M21 4 3 11l6 2 2 6 3-4 5 4z"/><path d="m9 13 12-9"/>'),
  warn: svg('<path d="M12 3 2 20h20z"/><path d="M12 10v4"/><path d="M12 17h.01"/>'),
  phone: svg('<path d="M5 3h4l2 5-3 2a11 11 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2A17 17 0 0 1 3 5a2 2 0 0 1 2-2z"/>'),
};

export function mount(root: HTMLElement, { lang }: Ctx) {
  const t: Tx = T[lang] ?? T.uk;
  document.head.insertAdjacentHTML('beforeend', `<style>${CSS}${PAGE_CSS}</style>`);
  const stageHost = root.querySelector<HTMLElement>('[data-stage]')!;
  const params = root.querySelector<HTMLElement>('[data-params]')!;
  const accBase = (location.pathname.match(/^\/(ru|en)\//)?.[0] ?? '/') + 'account/';
  const acc = accBase + 'review/';
  const auth = () => (window as unknown as { gen3dAuth?: { require(m?: string): Promise<boolean> } }).gen3dAuth;

  const api = async <R>(path: string, init?: RequestInit & { json?: unknown }): Promise<R> => {
    const r = await fetch('/api/review/' + path, {
      credentials: 'same-origin', ...init,
      ...(init?.json !== undefined ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.json) } : {}),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j.error || 'HTTP ' + r.status), { status: r.status, body: j });
    return j as R;
  };

  // полотно модели — своё на каждый показ; прежнее останавливаем
  let view: View | null = null;
  const idle = Object.assign(document.createElement('div'), { className: 'rv-idle', textContent: t.stageDrop });
  const dropStage = () => { view?.dispose(); view = null; stageHost.querySelectorAll('.rv-holder,.rv-cab').forEach((x) => x.remove()); };
  const freshStage = () => {
    dropStage();
    idle.remove();
    const s = Object.assign(document.createElement('div'), { className: 'rv-holder' });
    s.style.cssText = 'position:absolute;inset:0';
    stageHost.append(s);
    return s;
  };
  const p = Object.assign(document.createElement('div'), { className: 'rv-p' });
  params.replaceChildren(p);
  const clearHash = () => history.replaceState(null, '', location.pathname + location.search);

  // ---------- воркер: разбор и сжатие ----------
  let worker: Worker | null = null;
  const call = <R>(msg: unknown, transfer: Transferable[] = []) => new Promise<R>((res, rej) => {
    worker ??= new Worker(import.meta.url.replace(/[^/]*$/, 'worker.js'), { type: 'module' });
    worker.onmessage = (e) => (e.data.type === 'error' ? rej(new Error(e.data.message)) : res(e.data as R));
    worker.onerror = (e) => { rej(new Error(e.message)); worker?.terminate(); worker = null; };
    worker.postMessage(msg, transfer);
  });

  // Поле выбора файла в box: файл → (выбор объектов) → сжатые части → ready(prep, ui). take — для перетаскивания на полотно.
  type Ui = { status(text: string, frac?: number): void; fail(text: string): void };
  function picker(box: HTMLElement, hint: string, ready: (prep: Prep, ui: Ui) => void, local = false) {
    box.insertAdjacentHTML('beforeend', `<label class="rv-drop" data-drop><input type="file" accept=".3mf,.stl" hidden data-file><span class="btn primary">${t.choose3d}</span><small>${hint}</small></label><div data-step hidden></div>`);
    const drop = box.querySelector<HTMLElement>('[data-drop]')!, input = box.querySelector<HTMLInputElement>('[data-file]')!, step = box.querySelector<HTMLElement>('[data-step]')!;
    input.onchange = () => { if (input.files?.[0]) take(input.files[0]); input.value = ''; };
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('over'));
    drop.addEventListener('drop', (e) => { e.preventDefault(); drop.classList.remove('over'); const f = e.dataTransfer?.files[0]; if (f) take(f); });
    const ui: Ui = {
      status(text, frac) {
        step.hidden = false;
        step.innerHTML = `<p class="rv-term" role="status">${esc(text)}</p>${frac != null ? `<div class="rv-bar"><i style="width:${Math.round(frac * 100)}%"></i></div>` : ''}`;
      },
      fail(text) { step.hidden = false; step.innerHTML = `<p class="rv-msg">${esc(text)}</p>`; drop.hidden = false; },
    };

    async function take(file: File) {
      if (!/\.(3mf|stl)$/i.test(file.name)) return ui.fail(t.badFile);
      if (file.size > MAX_SRC) return ui.fail(t.srcBig);
      drop.hidden = true;
      ui.status(t.reading);
      const name = file.name.replace(/\.[^.]+$/, '');
      let o: { kind: '3mf' | 'stl'; items: Pick[]; colors: string[] };
      try { o = await call({ type: 'open', buf: await file.arrayBuffer(), name }); }
      catch (e) { console.error(e); return ui.fail(t.badFile); }
      if (!o.items.length) return ui.fail(t.badFile);
      if (o.items.length === 1) return build(name, o, [0]);
      // несколько объектов — какие показать
      step.innerHTML = `<p class="rv-term">${local ? t.pickV : t.pick}</p><label class="rv-picks" style="flex-direction:row"><input type="checkbox" data-all checked> <b>${t.all}</b></label>
<div class="rv-picks">${o.items.map((it, i) => `<label><input type="checkbox" value="${i}" checked> ${esc(it.name)}${it.copies > 1 ? ' ' + fill(t.copies, { n: it.copies }) : ''}</label>`).join('')}</div>
<div class="rv-row"><button type="button" class="btn primary" data-go>${local ? t.openV : t.go}</button><button type="button" class="btn ghost" data-no>${t.cancel}</button></div>`;
      const boxes = [...step.querySelectorAll<HTMLInputElement>('.rv-picks input[value]')];
      const all = step.querySelector<HTMLInputElement>('[data-all]')!;
      all.onchange = () => boxes.forEach((b) => { b.checked = all.checked; });
      step.querySelector<HTMLElement>('[data-no]')!.onclick = () => { step.hidden = true; drop.hidden = false; };
      step.querySelector<HTMLElement>('[data-go]')!.onclick = () => {
        const sel = boxes.filter((b) => b.checked).map((b) => +b.value).slice(0, MAX_ITEMS);
        if (sel.length) build(name, o, sel);
      };
    }

    async function build(name: string, o: { items: Pick[]; colors: string[] }, sel: number[]) {
      const prep: Prep = { name, items: [], colors: o.colors, parts: [] };
      try {
        for (let k = 0; k < sel.length; k++) {
          ui.status(fill(t.building, { i: k + 1, n: sel.length }), k / sel.length);
          const r = await call<{ buf: ArrayBuffer; size: number[] }>({ type: 'build', index: sel[k] });
          prep.parts.push(new Uint8Array(r.buf));
          prep.items.push({ name: o.items[sel[k]].name, size: r.size, copies: o.items[sel[k]].copies });
        }
      } catch (e) { console.error(e); return ui.fail(t.badFile); }
      step.hidden = true; drop.hidden = false;
      ready(prep, ui);
    }
    return { take };
  }

  // превью 256 px WebP: с открытого вида (тот ракурс, что на экране) или первая деталь на своём полотне
  async function thumbOf(prep: Prep, from: View | null) {
    try {
      let v = from;
      if (!v) {
        v = view = createView(freshStage(), { dimL: t.dimL, mm: t.mm, markerL: t.marker });
        v.setDims(false);
        v.show(await decode(prep.parts[0].slice().buffer), prep.colors);
        // кадр-другой на размер полотна; во вкладке в фоне кадров нет — не ждём дольше 300 мс (snapshot рисует сам)
        await Promise.race([new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r))), new Promise((r) => setTimeout(r, 300))]);
      }
      const blob = await new Promise<Blob | null>((r) => v!.snapshot(256).toBlob(r, 'image/webp', 0.82));
      if (blob && blob.type === 'image/webp' && blob.size < 190 * 1024) return new Uint8Array(await blob.arrayBuffer());
    } catch (e) { console.warn('thumb', e); }
    return new Uint8Array(0);
  }
  // [u32 длина заголовка][JSON][части E3M1][превью] — формат uploadApi
  function bodyOf(prep: Prep, thumb: Uint8Array) {
    const total = prep.parts.reduce((s, x) => s + x.length, 0);
    const head = new TextEncoder().encode(JSON.stringify({ items: prep.items, colors: prep.colors, parts: prep.parts.map((x) => x.length), thumb: thumb.length }));
    const body = new Uint8Array(4 + head.length + total + thumb.length);
    new DataView(body.buffer).setUint32(0, head.length, true);
    body.set(head, 4);
    let off = 4 + head.length;
    for (const x of prep.parts) { body.set(x, off); off += x.length; }
    body.set(thumb, off);
    return body;
  }
  // версия в проект; ошибка — текст для человека
  async function sendVersion(id: number, body: Uint8Array<ArrayBuffer>): Promise<string | null> {
    try {
      const r = await fetch(`/api/review/p/${id}/upload`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/octet-stream' }, body });
      const j = await r.json().catch(() => ({}));
      return r.ok ? null : j.error === 'TOO_LARGE' ? t.tooBig : t.err;
    } catch { return t.err; }
  }
  // новый проект по имени файла + первая версия; не загрузилось — пустой проект убираем, чтобы не занимал лимит
  async function newProject(title: string, body: Uint8Array<ArrayBuffer>, say: (text: string, err?: boolean) => void) {
    say(t.sending);
    let id: number;
    try { id = (await api<{ id: number }>('p', { json: { title } })).id; }
    catch (e) {
      const b = (e as { body?: { error?: string; n?: number } }).body;
      say(b?.error === 'LIMIT' ? fill(t.limit, { n: b.n ?? '' }) : t.err, true);
      return false;
    }
    const err = await sendVersion(id, body);
    if (err) { await api(`p/${id}/delete`, { json: {} }).catch(() => {}); say(err, true); return false; }
    toast(fill(t.uploaded, { n: 1 }));
    open(id);
    return true;
  }

  // ---------- начало: открыть файл; ниже — свои проекты или вход ----------
  let stageDrop: ((f: File) => void) | null = null;
  function home() {
    dropStage();
    stageHost.append(idle);
    p.innerHTML = `<div class="rv-box" data-open><h3>${t.viewT}</h3></div><div class="rv-mine" data-mine></div>`;
    stageDrop = picker(p.querySelector<HTMLElement>('[data-open]')!, t.dropV, (prep) => localView(prep), true).take;
    return mine(p.querySelector<HTMLElement>('[data-mine]')!);
  }
  // файл можно бросить и прямо на пустое полотно
  stageHost.addEventListener('dragover', (e) => { if (idle.isConnected && stageDrop) { e.preventDefault(); idle.classList.add('over'); } });
  stageHost.addEventListener('dragleave', () => idle.classList.remove('over'));
  stageHost.addEventListener('drop', (e) => {
    if (!idle.isConnected || !stageDrop) return;
    e.preventDefault(); idle.classList.remove('over');
    const f = e.dataTransfer?.files[0];
    if (f) stageDrop(f);
  });

  const sayIn = (box: HTMLElement) => (text: string, err?: boolean) => {
    let m = box.querySelector<HTMLElement>('[data-say]');
    if (!m) { m = Object.assign(document.createElement('p'), { className: 'rv-msg' }); m.dataset.say = ''; box.prepend(m); }
    m.setAttribute('role', 'status');
    m.textContent = text; m.className = 'rv-msg' + (err ? '' : ' ok');
  };

  async function mine(box: HTMLElement): Promise<void> {
    let d: { term: Term; profile: boolean; projects: Row[] };
    try { d = await api('p'); }
    catch (e) {
      if ((e as { status?: number }).status !== 401) { box.innerHTML = `<p class="rv-msg">${t.err}</p>`; return; }
      box.innerHTML = `<div class="rv-intro"><h2>${t.introT}</h2><p>${t.introP}</p><button type="button" class="btn ghost" data-in>${t.signIn}</button></div>`;
      box.querySelector<HTMLElement>('[data-in]')!.onclick = async () => { if (await auth()?.require('plain')) mine(box); };
      return;
    }
    if (location.hash.startsWith('#p')) { const id = +location.hash.slice(2); if (d.projects.some((x) => x.id === id)) return open(id); clearHash(); }
    box.innerHTML = `<h3>${t.mine}</h3><p class="rv-term">${fill(d.term.projects === null ? t.termUnl : t.term, { n: d.term.projects ?? '', d: d.term.days })}</p>`
      + (d.profile ? '' : `<p class="rv-tip">${t.profileHint} <a href="${acc}">${t.profileLink}</a></p>`)
      + `<p class="rv-msg" role="status" data-say></p>`
      + (d.projects.length ? `<ul class="rv-projs">${d.projects.map((r) => `<li><button type="button" class="rv-proj" data-open="${r.id}">`
        + (r.thumb ? `<img src="${r.thumb}" alt="" loading="lazy">` : '<span class="ph"></span>')
        + `<span><b>${esc(r.title)}</b><small>${r.versions ? fill(t.till, { date: day(r.expires, lang) }) : t.noModel}${r.open ? ` · <span class="new">${fill(t.openN, { n: r.open })}</span>` : ''}</small></span></button></li>`).join('')}</ul>`
        : `<p class="rv-empty">${t.emptyV}</p>`);
    box.querySelectorAll<HTMLElement>('[data-open]').forEach((b) => b.onclick = () => open(+b.dataset.open!));
    // вернулись после входа (Google, ссылка из письма) — загрузка, начатая до входа, продолжается сама
    const s = await stashTake();
    if (s) resume(box, s);
  }
  function resume(box: HTMLElement, s: Stash) {
    const say = sayIn(box);
    newProject(s.title, s.body, (text, err) => say(err ? text : fill(t.resume, { name: s.title }), err));
  }

  // ---------- свой файл без входа: модель, детали, линейка ----------
  function localView(prep: Prep) {
    clearHash();
    p.innerHTML = `<button type="button" class="rv-back" data-back>${t.toStart}</button>
<div class="rv-box"><h3>${t.localT}</h3><b class="rv-fname">${esc(prep.name)}</b><p class="rv-term">${t.localNote}</p>
<div class="rv-row"><button type="button" class="btn primary" data-share>${t.share}</button><button type="button" class="btn ghost" data-other>${t.other}</button></div></div>
<div data-side></div>`;
    stageDrop = null;
    const st = freshStage();
    const data: Data = { versions: [{ id: 0, n: 1, items: prep.items, colors: prep.colors, at: new Date().toISOString() }], comments: [], colors: [], palette: [] };
    view = openPanel({
      stage: st, side: p.querySelector<HTMLElement>('[data-side]')!, lang, t, role: 'local', data,
      api: { file: () => '', buf: (_v, i) => Promise.resolve(prep.parts[i].slice().buffer) },
    }).view;
    st.append(Object.assign(document.createElement('div'), { className: 'rv-badge', textContent: t.localBadge }));
    p.querySelector<HTMLElement>('[data-back]')!.onclick = () => { home(); };
    // выбор файла — в том же нажатии, иначе браузер не откроет окно
    p.querySelector<HTMLElement>('[data-other]')!.onclick = () => { home(); p.querySelector<HTMLInputElement>('[data-file]')?.click(); };
    p.querySelector<HTMLElement>('[data-share]')!.onclick = () => share(prep);
  }

  // окно «Показать заказчику»: кто увидит, сколько хранится; без входа — вход, потом загрузка
  async function share(prep: Prep) {
    let info: { signed: boolean; term: { projects: number | null; days: number }; used: number };
    try { info = await api('term'); } catch { toast(t.err); return; }
    const body = bodyOf(prep, await thumbOf(prep, view));
    const big = body.length > MAX_UP;
    const full = info.signed && info.term.projects !== null && info.used >= info.term.projects;
    const dlg = Object.assign(document.createElement('dialog'), { className: 'rv-dlg' });
    dlg.setAttribute('aria-labelledby', 'rvShareT');
    dlg.innerHTML = `<h2 id="rvShareT">${t.shareT}</h2><p>${fill(t.shareP, { d: info.term.days })}</p>`
      + (big ? `<p class="rv-msg">${t.tooBig}</p>` : full ? `<p class="rv-msg">${fill(t.limit, { n: info.term.projects! })}</p>` : '')
      + `<div class="rv-row">${big || full ? '' : `<button type="button" class="btn primary" data-ok>${info.signed ? t.shareUp : t.shareIn}</button>`}`
      + `<button type="button" class="btn ghost" data-no>${t.cancel}</button></div><p class="rv-msg" role="status" data-say></p>`;
    document.body.append(dlg);
    dlg.addEventListener('close', () => dlg.remove());
    dlg.showModal();
    dlg.querySelector<HTMLElement>('[data-no]')!.onclick = () => dlg.close();
    const ok = dlg.querySelector<HTMLButtonElement>('[data-ok]');
    if (!ok) return;
    ok.onclick = async () => {
      ok.disabled = true;
      if (info.signed) {
        if (await newProject(prep.name, body, sayIn(dlg))) dlg.close(); else ok.disabled = false;
        return;
      }
      let kept = false;
      try { await stashPut({ at: Date.now(), title: prep.name, body }); kept = true; } catch { /* приватный режим: продолжим из памяти, если вход пройдёт окном */ }
      dlg.close(); // у входа своё окно — два модальных окна не нужны
      if (!(await auth()?.require('plain'))) { if (kept) stashDel(); return; }
      // вход прошёл окном. home() → mine() сам заберёт копию и продолжит; без копии — загружаем из памяти
      if (kept) { home(); return; }
      dropStage();
      await home();
      resume(p.querySelector<HTMLElement>('[data-mine]')!, { at: 0, title: prep.name, body });
    };
  }

  // ---------- проект ----------
  async function open(id: number): Promise<void> {
    let d: Project;
    try { d = await api(`p/${id}`); } catch { clearHash(); home(); return; }
    history.replaceState(null, '', location.pathname + location.search + '#p' + id);
    stageDrop = null;
    p.innerHTML = `<form class="rv-form" data-meta><input name="title" maxlength="120" required value="${esc(d.title)}" aria-label="${esc(t.titlePh)}" class="rv-in" style="font-weight:700">
<textarea name="note" maxlength="2000" placeholder="${esc(t.note)}" aria-label="${esc(t.note)}">${esc(d.note)}</textarea></form>
<div class="rv-box" data-link></div>
<div data-side></div>
<div class="rv-box" data-up></div>
<button type="button" class="btn ghost rv-danger" data-del>${t.del}</button>`;
    // плашка «Мой кабинет › Мои 3D-проекты › название» с доступом справа — видно, что это проект из кабинета, а не просто просмотр
    const cab = Object.assign(document.createElement('div'), { className: 'rv-cab' });
    cab.innerHTML = `<nav class="rv-path" aria-label="${esc(t.cabinet)}"><a href="${accBase}projects/">${ICON.user}${t.cabinet}</a><span class="sep" aria-hidden="true">›</span>`
      + `<button type="button" class="mid" data-back>${t.mine3d}</button><span class="sep mid" aria-hidden="true">›</span><b data-crumb>${esc(d.title)}</b></nav>`
      + `<span class="rv-st" role="status" data-acc></span>`;
    cab.querySelector<HTMLElement>('[data-back]')!.onclick = () => { clearHash(); home(); };
    const accEl = cab.querySelector<HTMLElement>('[data-acc]')!;
    const paintAcc = () => {
      const text = d.link ? fill(t.stLink, { date: day(d.expires, lang) }) : d.closed ? t.stClosed : t.stPriv;
      accEl.innerHTML = (d.link ? ICON.link : ICON.lock) + `<span class="l">${esc(text)}</span>`;
      accEl.title = text; accEl.setAttribute('aria-label', text);
      accEl.classList.toggle('on', !!d.link);
    };
    const meta = p.querySelector<HTMLFormElement>('[data-meta]')!;
    meta.addEventListener('change', async () => {
      const title = (meta.elements.namedItem('title') as HTMLInputElement).value;
      try {
        await api(`p/${id}`, { json: { title, note: (meta.elements.namedItem('note') as HTMLTextAreaElement).value } });
        d.title = title.trim() || d.title;
        cab.querySelector<HTMLElement>('[data-crumb]')!.textContent = d.title;
        toast(t.saved);
      } catch { toast(t.err); }
    });
    meta.onsubmit = (e) => e.preventDefault();
    p.querySelector<HTMLElement>('[data-del]')!.onclick = async () => {
      if (!confirm(t.delQ)) return;
      try { await api(`p/${id}/delete`, { json: {} }); clearHash(); home(); } catch { toast(t.err); }
    };
    uploadBox(p.querySelector<HTMLElement>('[data-up]')!, d);
    linkBox(p.querySelector<HTMLElement>('[data-link]')!, d, paintAcc);
    const side = p.querySelector<HTMLElement>('[data-side]')!;
    if (d.versions.length) {
      view = openPanel({
        stage: freshStage(), side, lang, t, role: 'owner', data: d,
        api: {
          file: (v, i) => `/api/review/f/${v}/${i}`,
          reply: (b) => api<{ comment: Comment }>(`p/${id}/reply`, { json: b }).then((r) => r.comment),
          done: (cid, done) => api(`c/${cid}`, { json: { done } }).then(() => {}),
        },
      }).view;
    } else { dropStage(); stageHost.append(idle); }
    stageHost.append(cab);
  }

  // ---------- ссылка ----------
  function linkBox(box: HTMLElement, d: Project, changed: () => void) {
    const langSel = (cur: Lang) => `<label class="rv-row" style="font-size:14px">${t.langL}<select data-lang>${(['uk', 'ru', 'en'] as Lang[]).map((l) => `<option value="${l}"${l === cur ? ' selected' : ''}>${LANG_NAMES[l]}</option>`).join('')}</select></label>`;
    changed();
    if (!d.versions.length) { box.innerHTML = `<h3>${t.link}</h3><p class="rv-empty">${t.noVersion}</p>${profBox(d)}`; return; }
    if (!d.link) {
      box.innerHTML = `<h3>${t.link}</h3>${langSel(lang)}<div class="rv-row"><button type="button" class="btn primary" data-make>${t.makeLink}</button></div>${profBox(d)}`;
      box.querySelector<HTMLElement>('[data-make]')!.onclick = async () => {
        try { const r = await api<{ url: string; pin: string; lang: Lang }>(`p/${d.id}/link`, { json: { action: 'new', lang: box.querySelector<HTMLSelectElement>('[data-lang]')!.value } }); d.link = r; linkBox(box, d, changed); }
        catch { toast(t.err); }
      };
      return;
    }
    const l = d.link;
    box.innerHTML = `<h3>${t.link}</h3><p class="rv-link">${esc(l.url)}<br>PIN: <b>${esc(l.pin)}</b></p>
<div class="rv-row"><button type="button" class="btn primary" data-copy>${t.copy}</button></div>${langSel(l.lang)}
<div class="rv-row"><button type="button" class="btn ghost" data-re>${t.reissue}</button><button type="button" class="btn ghost rv-danger" data-close>${t.closeLink}</button></div>${profBox(d)}`;
    box.querySelector<HTMLElement>('[data-copy]')!.onclick = async () => {
      const text = fill(t.copyText, { title: d.title, url: l.url, pin: l.pin });
      try { await navigator.clipboard.writeText(text); }
      catch { const a = Object.assign(document.createElement('textarea'), { value: text }); document.body.append(a); a.select(); document.execCommand('copy'); a.remove(); }
      toast(t.copied);
    };
    box.querySelector<HTMLSelectElement>('[data-lang]')!.onchange = async (e) => {
      const v = (e.target as HTMLSelectElement).value as Lang;
      try { await api(`p/${d.id}/link`, { json: { action: 'lang', lang: v } }); l.lang = v; toast(t.saved); } catch { toast(t.err); }
    };
    box.querySelector<HTMLElement>('[data-re]')!.onclick = async () => {
      if (!confirm(t.reissueQ)) return;
      try { d.link = await api(`p/${d.id}/link`, { json: { action: 'new', lang: l.lang } }); linkBox(box, d, changed); } catch { toast(t.err); }
    };
    box.querySelector<HTMLElement>('[data-close]')!.onclick = async () => {
      if (!confirm(t.closeQ)) return;
      try { await api(`p/${d.id}/link`, { json: { action: 'close' } }); d.link = null; d.closed = true; linkBox(box, d, changed); } catch { toast(t.err); }
    };
  }

  // что заказчик увидит в шапке своей страницы; правится в кабинете, оттуда ссылка обратно (?back=)
  function profBox(d: Project) {
    const pr = d.profile, href = `${acc}?back=${d.id}`;
    const pal = d.palette.length ? '' : `<p class="rv-term">${t.palEmpty}</p>`;
    if (!pr.name && !pr.logo && !pr.phone && !pr.telegram)
      return `<div class="rv-prof warn"><p class="rv-pwarn">${ICON.warn}<span>${t.profEmpty}</span></p><a class="btn ghost rv-pfill" href="${href}">${t.profFill}</a>${pal}</div>`;
    return `<div class="rv-prof"><p class="rv-term">${t.profSees}</p><div class="rv-pcard">`
      + (pr.logo ? `<img src="${esc(pr.logo)}" alt="" width="40" height="40">` : '')
      + `<b>${esc(pr.name)}</b>`
      + (pr.telegram ? `<i title="${t.tgL}" aria-label="${t.tgL}">${ICON.tg}</i>` : '')
      + (pr.phone ? `<i title="${t.phoneL}" aria-label="${t.phoneL}">${ICON.phone}</i>` : '')
      + `<a href="${href}">${t.profEdit}</a></div>${pal}</div>`;
  }

  // ---------- новая версия в проект ----------
  function uploadBox(box: HTMLElement, d: Project) {
    box.innerHTML = `<h3>${d.versions.length ? t.uploadNew : t.upload}</h3>${d.versions.length >= 4 ? `<p class="rv-term">${t.versionsMax}</p>` : ''}`;
    picker(box, t.drop, async (prep, ui) => {
      ui.status(t.sending, 1);
      const body = bodyOf(prep, await thumbOf(prep, null));
      if (body.length > MAX_UP) return ui.fail(t.tooBig);
      const err = await sendVersion(d.id, body);
      if (err) return ui.fail(err);
      toast(fill(t.uploaded, { n: d.versions.length ? d.versions[d.versions.length - 1].n + 1 : 1 }));
      open(d.id);
    });
  }

  home();
  return {
    // главная витрина и «Снять для главной» ждут модель — у этого сервиса её нет
    model: () => Promise.reject(new Error('review: нет модели для скачивания')),
  };
}
